import React, { useState } from 'react';
import "./styles.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faHome, faUser } from "@fortawesome/free-solid-svg-icons";
import FloatingActions, { FloatingAction } from "../FloatingActions";
import Card from "../Card";
import DateTimeInput from "../DateTimeInput";
import useAppData from "@/store/useAppData";
import axios from "axios";
import DialogWindow from '../DialogWindow';

export interface NewRecordCardProps {
    onSave: (newInventoryId: string) => void;
    inventoryId?: number;
}

const NewInventoryCardDeice: React.FC<NewRecordCardProps> = ({
    onSave,
    inventoryId
}) => {

    const loggedUser = useAppData(state => state.user);
    const [name, setName] = React.useState("");
    const [date, setDate] = React.useState<Date | null>(new Date());
    const [error, setError] = React.useState<string | null>(null);
    const [showDialog, setShowDialog] = useState<boolean>(false);
    const [dialogText, setDialogText] = useState<string>("");

    const validate = () => {
        const n = name.trim();

        if (!n || !date) return "Vyplňte povinná pole Název a Datum";
        return null;
    };

    const onCreate = async () => {
        setError(null);
        const validationError = validate();
        if (validationError) {
            setError(validationError);
            return;
        }

        let success = true;
        const resultNewInventoryId = await insertNewInventory(name, date.toISOString());

        if (!resultNewInventoryId) {
            success = false;
        } else {

            let resultmoveStockItemToInventorySuccess = false;

            if (inventoryId) {
                resultmoveStockItemToInventorySuccess = await copyInventoryItems(resultNewInventoryId, inventoryId);
            } else {
                resultmoveStockItemToInventorySuccess = await createNewInventoryItems(resultNewInventoryId);
            }

            if (!resultmoveStockItemToInventorySuccess) {
                success = false;
            }
        }

        if (success) {
            onSave(resultNewInventoryId);
        } else {
            setDialogText("Došlo k chybě při vytváření nové inventury");
            setShowDialog(true);
        }
    };

    const actions: FloatingAction[] = [
        {
            key: "cancel",
            label: "Zrušit",
            icon: <FontAwesomeIcon icon={faUser} />,
            onClick: () => onSave(null),
        },
        {
            key: "save",
            label: "Vytvořit",
            icon: <FontAwesomeIcon icon={faHome} />,
            onClick: onCreate,
        },
    ];

    const insertNewInventory = async (description: string, date: string) => {
        try {
            const res = await axios({
                method: "post",
                url: "/api/inventorydeice/insert",
                data: {
                    description: description,
                    date: date,
                    created_by: loggedUser?.id 
                },
            });

            return res.data?.id;
        } catch (err) {
            console.error("Failed to insert inventory item", err);
            throw err;
        }
    };

    const copyInventoryItems = async (newInventoryId: string, inventoryId: number) => {
        try {
            const res = await axios({
                method: "post",
                url: "/api/inventorydeice/copyItems",
                data: {
                    newInventoryId: newInventoryId,
                    inventoryId: inventoryId
                },
            });

            return res.data?.ok;
        } catch (err) {
            console.error("Failed to move stock items to inventory", err);
            throw err;
        }
    };

    const createNewInventoryItems = async (newInventoryId: string) => {
        try {
            const res = await axios({
                method: "post",
                url: "/api/inventorydeice/createNewItems",
                data: {
                    newInventoryId: newInventoryId
                },
            });

            return res.data?.ok;
        } catch (err) {
            console.error("Failed to move stock items to inventory", err);
            throw err;
        }
    };

    const toDateTimeLocal = (date: Date) => {
        const pad = (value: number) => String(value).padStart(2, "0");

        return (
            `${date.getFullYear()}-` +
            `${pad(date.getMonth() + 1)}-` +
            `${pad(date.getDate())}T` +
            `${pad(date.getHours())}:` +
            `${pad(date.getMinutes())}`
        );
    };

    return (
        <>
            {showDialog &&
                <DialogWindow
                    displayDialog={showDialog}
                    setDisplayDialog={setShowDialog}
                    title={dialogText}
                    cancelText={"OK"}
                />
            }

            <Card title="Nová inventura">
                <div className="nec-stack">
                    <div className="nec-block">
                        <div className="nec-header">Název</div>
                        <div className="nec-control">
                            <input
                                className="nec-input"
                                type="text"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                placeholder="Název inventury..."
                                autoComplete="off"
                            />
                        </div>
                    </div>

                    <div className="nec-block">
                        <div className="nec-header">Datum</div>
                        <div className="nec-control">
                            <DateTimeInput
                                className="nec-input"
                                value={date ? toDateTimeLocal(date) : ""}
                                onChange={value => setDate(value ? new Date(value) : null)}
                            />
                        </div>
                    </div>

                    {error ? <div className="form-error">{error}</div> : null}
                </div>

                {actions?.length ? (
                    <div className="item-list-actions">
                        <FloatingActions actions={actions} />
                    </div>
                ) : null}
            </Card>
        </>);
};

export default NewInventoryCardDeice;
